/**
 * Classifies scanned/pasted QR content for UI hinting ONLY.
 *
 * The server resolves and validates everything — this module never verifies a
 * signature or trusts a decoded field for authorization. It exists so the scan
 * console can (a) tell a static card from a dynamic token in the interface
 * copy, and (b) extract the opaque credential reference a dynamic token names,
 * which is what the pre-scan identity preview endpoint is keyed by.
 */

export interface QrContentClassification {
  kind: 'static' | 'dynamic' | 'unknown';
  /** The `crd_…` reference when one can be read; null for malformed input. */
  credentialPublicId: string | null;
}

export function classifyQrContent(raw: string): QrContentClassification {
  const value = raw.trim();
  if (value.startsWith('crd_') && value.length > 4) {
    return { kind: 'static', credentialPublicId: value };
  }
  if (value.startsWith('cvq.')) {
    // cvq.<base64url(payload JSON)>.<base64url(hmac)> — the payload's `c` field
    // names the credential. Decoding is best-effort: a token we cannot read is
    // still submitted verbatim to the server, which is the actual judge.
    const parts = value.split('.');
    if (parts.length === 3) {
      const payload = decodeBase64UrlJson(parts[1]);
      if (payload && typeof payload.c === 'string' && payload.c.startsWith('crd_')) {
        return { kind: 'dynamic', credentialPublicId: payload.c };
      }
    }
    return { kind: 'dynamic', credentialPublicId: null };
  }
  return { kind: 'unknown', credentialPublicId: null };
}

function decodeBase64UrlJson(segment: string): Record<string, unknown> | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
